import { Stack, Tab, Tabs, Typography, useTheme } from "@mui/material";
import { ReactNode, useMemo, useState } from "react";
import { ProfileSettings } from "./components/profile-settings";
import { Button, TooltipShortcut } from "@itk/mui-components";
import LogoutIcon from "@mui/icons-material/Logout";
import { authorizationGateActions } from "src/components/authorization-gate/authorization-gate-store";
import { connect } from "react-redux";
import { profileTabs, profileTabsValue } from "./profile-types";
import { profileSettingsPageK, useProfileSettingsPageT } from "src/translation-hooks/profile-settings-page";
import ProfileInfo from "./components/profile-info/profile-info";
import { useGetContactPersonDetail } from "src/hooks";
import { ChangePassword } from "./components/change-password";
import { NullGuid } from "@itk/typescript-utils";

const ownActions = {
  logoutRequest: authorizationGateActions.logoutRequest
};

type Props = typeof ownActions;

const ProfileBase = ({ logoutRequest }: Props) => {
  const theme = useTheme();
  const profileSettingsPageT = useProfileSettingsPageT();

  const [selectedTab, setSelectedTab] = useState<profileTabsValue>("account");
  const { contactPersonDetail } = useGetContactPersonDetail();
  const currentTab = useMemo((): { component: ReactNode; resetChanges?: () => void; onSave?: () => void } => {
    switch (selectedTab) {
      case "account":
        return {
          component: <ProfileInfo />
        };
      case "general":
        return {
          component: <ProfileSettings />
        };
      case "changePassword":
        return {
          component: <ChangePassword />
        };
      default:
        return { component: <></> };
    }
  }, [selectedTab]);

  return (
    <Stack>
      <Stack direction="row" justifyContent="space-between">
        <Typography variant="h2" color={theme.palette.customColors.darkGrey}>
          {profileSettingsPageT.profileSettings}
        </Typography>
        <TooltipShortcut shortcut={{ action: logoutRequest, shortCutKey: "l", modifier: "alt" }}>
          <Button
            variant="contained"
            startIcon={<LogoutIcon />}
            t={profileSettingsPageT}
            field="logout"
            k={profileSettingsPageK}
            onClick={() => {
              logoutRequest();
            }}
          />
        </TooltipShortcut>
      </Stack>
      <Stack>
        <Tabs
          value={selectedTab}
          onChange={(_, newValue) => {
            setSelectedTab(newValue);
          }}
          sx={{
            "& .MuiTabs-indicator": {
              backgroundColor: theme.palette.primary.main
            }
          }}
        >
          {profileTabs
            .filter(tab => !(tab === "general" && contactPersonDetail?.contactPersonId === NullGuid))
            .map(tab => (
              <Tab key={tab} data-itk-test={profileSettingsPageT[tab]} value={tab} label={profileSettingsPageT[tab]} />
            ))}
        </Tabs>
      </Stack>
      <Stack mt={2}>{currentTab.component}</Stack>
    </Stack>
  );
};

export const Profile = connect(undefined, ownActions)(ProfileBase);
